import React from 'react';
import { Plus, Minus, X, ShoppingCart } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import CurrencyInput from './CurrencyInput';

const ItemListComponent = ({
  items = [],
  itemQuantities = {},
  onQuantityChange,
  onQuantityIncrement,
  onQuantityDecrement,
  onRemoveItem,
  title = "Items",
  emptyMessage = "No items added yet",
  emptyDescription = "Search and select items to add them to the list.",
  showStockStatus = true,
  showExpiryStatus = true,
  showDiscount = true,
  showPricePerUnit = true,
  showItemDetails = true,
  allowQuantityEdit = true,
  maxQuantityPerItem = null,
  className = "",
  compact = false,
  highlightZeroPrice = false,
  // Optional per-item unit price ({ [item.ID]: price }) that overrides the catalogue price.
  // Items listed here get an editable price box when onPriceChange is given.
  unitPrices = {},
  onPriceChange,
}) => {
  const { formatPrice } = useCurrency();

  // Helper functions for item status
  const getStockStatus = (item) => {
    const currentStock = parseFloat(item.Stock) || 0;
    const minQuantity = parseFloat(item.MinimunQuantity) || 0;

    if (currentStock <= 0) return { status: 'out', color: 'bg-red-100 text-red-800', text: 'Out of Stock' };
    if (currentStock <= minQuantity) return { status: 'low', color: 'bg-yellow-100 text-yellow-800', text: 'Low Stock' };
    return { status: 'good', color: 'bg-green-100 text-green-800', text: 'In Stock' };
  };

  const getExpiryStatus = (item) => {
    if (!item.TimeToZero) return null;
    const expiryDate = new Date(item.TimeToZero);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry <= 0) return { status: 'expired', color: 'bg-red-100 text-red-800', text: 'Expired' };
    if (daysUntilExpiry <= 7) return { status: 'expiring', color: 'bg-yellow-100 text-yellow-800', text: `${daysUntilExpiry} days left` };
    return null;
  };

  const hasUnitPrice = (item) => unitPrices[item.ID] !== undefined;

  const getUnitPrice = (item) =>
    hasUnitPrice(item) ? Number(unitPrices[item.ID]) || 0 : parseFloat(item.SalePrice || item.Price || 0);

  const getItemTotal = (item, quantity) => quantity * getUnitPrice(item);

  const renderUnitPrice = (item, textClass) => {
    if (onPriceChange && hasUnitPrice(item)) {
      return (
        <label className={`flex items-center justify-end gap-1 ${textClass} text-gray-500`}>
          <span>Rs</span>
          <CurrencyInput
            value={getUnitPrice(item)}
            onChange={(value) => onPriceChange(item.ID, value)}
            className="w-20 px-1.5 py-0.5 text-xs text-right text-gray-900 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            title="Edit the price per unit"
            aria-label={`Price per unit for ${item.Name}`}
          />
          <span>each</span>
        </label>
      );
    }
    return (
      <p className={`${textClass} text-gray-500`}>
        {formatPrice(item.SalePrice || item.Price)} each
      </p>
    );
  };

  const getAvailableStock = (item) => {
    const availableRaw = parseFloat(item.Stock);
    return Number.isFinite(availableRaw) ? Math.max(0, Math.floor(availableRaw)) : 0;
  };

  if (items.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingCart className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{emptyMessage}</h3>
          <p className="text-gray-500 text-sm">{emptyDescription}</p>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={`space-y-2 ${className}`}>
        {items.map((item, index) => {
          const quantity = itemQuantities[item.ID] || item.quantity || 1;
          const itemTotal = getItemTotal(item, quantity);
          const itemDiscount = parseFloat(item.Price - item.SalePrice || 0);
          const stockStatus = getStockStatus(item);
          const expiryStatus = getExpiryStatus(item);
          const available = getAvailableStock(item);
          const maxQuantity = maxQuantityPerItem || item.maxQuantity || available || Infinity;
          const hasZeroPrice = highlightZeroPrice && getUnitPrice(item) === 0;

          return (
            <div
              key={`${item.ID}-${index}`}
              className={`p-3 rounded-lg ${
                hasZeroPrice ? 'bg-red-50 border-2 border-red-500' : 'bg-gray-50 border border-gray-200'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Left: name + details */}
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 text-sm leading-tight break-words">{item.Name}</h4>
                  {item.NameUrdu && (
                    <p className="text-xs text-gray-500 mt-0.5" dir="rtl">{item.NameUrdu}</p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    {showPricePerUnit && (
                      <span className="text-xs text-gray-600">
                        {formatPrice(item.Price)} / {item.Unit || 'unit'}
                      </span>
                    )}
                    {showDiscount && itemDiscount > 0 && (
                      <span className="text-xs text-green-600 font-medium">
                        -{formatPrice(itemDiscount)} discount
                      </span>
                    )}
                    {showStockStatus && (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                        {stockStatus.text}
                      </span>
                    )}
                    {showExpiryStatus && expiryStatus && (
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${expiryStatus.color}`}>
                        {expiryStatus.text}
                      </span>
                    )}
                  </div>
                  {showItemDetails && (
                    <div className="mt-1 text-xs text-gray-500">
                      <span>ID: {item.ID}</span>
                      {item.Barcode && <span className="ml-2">Barcode: {item.Barcode}</span>}
                    </div>
                  )}
                </div>

                {/* Right: price + controls stacked */}
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-semibold text-blue-600 leading-tight">
                      Rs.{itemTotal.toFixed(2)}
                    </p>
                    {renderUnitPrice(item, 'text-xs leading-tight')}
                  </div>
                  <div className="flex items-center gap-1">
                    {allowQuantityEdit && (
                      <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg">
                        <button
                          type="button"
                          onClick={() => onQuantityDecrement && onQuantityDecrement(item.ID)}
                          disabled={quantity <= 1}
                          className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Decrease quantity"
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="px-1.5 text-sm font-medium text-gray-700 min-w-[1.5rem] text-center">
                          {quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => onQuantityIncrement && onQuantityIncrement(item.ID)}
                          disabled={quantity >= maxQuantity}
                          className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Increase quantity"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    {onRemoveItem && (
                      <button
                        type="button"
                        onClick={() => onRemoveItem(item.ID)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        title="Remove item"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {items.map((item, index) => {
        const quantity = itemQuantities[item.ID] || item.quantity || 1;
        const itemTotal = getItemTotal(item, quantity);
        const itemDiscount = parseFloat(item.Price - item.SalePrice || 0);
        const stockStatus = getStockStatus(item);
        const expiryStatus = getExpiryStatus(item);
        const available = getAvailableStock(item);
        const maxQuantity = maxQuantityPerItem || item.maxQuantity || available || Infinity;

        return (
          <div key={`${item.ID}-${index}`} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-shrink-0 w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-gray-200">
                <span className="text-sm font-semibold text-gray-600">
                  {item.Name?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 break-words">{item.Name}</h4>
                {item.NameUrdu && (
                  <p className="text-sm text-gray-500 mt-1" dir="rtl">{item.NameUrdu}</p>
                )}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {showPricePerUnit && (
                    <span className="text-sm text-gray-600">
                      {formatPrice(item.Price)} / {item.Unit || 'unit'}
                    </span>
                  )}
                  {showDiscount && itemDiscount > 0 && (
                    <span className="text-sm text-green-600 font-medium">
                      -{formatPrice(itemDiscount)} discount
                    </span>
                  )}
                  {showStockStatus && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stockStatus.color}`}>
                      {stockStatus.text}
                    </span>
                  )}
                  {showExpiryStatus && expiryStatus && (
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${expiryStatus.color}`}>
                      {expiryStatus.text}
                    </span>
                  )}
                </div>
                {showItemDetails && (
                  <div className="mt-1 text-xs text-gray-500">
                    <span>ID: {item.ID}</span>
                    {item.Barcode && <span className="ml-2">Barcode: {item.Barcode}</span>}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-right">
                <p className="text-sm font-semibold text-blue-600">
                  Rs.{itemTotal.toFixed(2)}
                </p>
                {renderUnitPrice(item, 'text-xs')}
              </div>
              <div className="flex items-center gap-2">
                {/* Quantity Controls */}
                {allowQuantityEdit && (
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg">
                    <button
                      type="button"
                      onClick={() => onQuantityDecrement && onQuantityDecrement(item.ID)}
                      disabled={quantity <= 1}
                      className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Decrease quantity"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="px-2 py-1 text-sm font-medium text-gray-700 min-w-[2rem] text-center">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => onQuantityIncrement && onQuantityIncrement(item.ID)}
                      disabled={quantity >= maxQuantity}
                      className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Increase quantity"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                )}
                {onRemoveItem && (
                  <button
                    type="button"
                    onClick={() => onRemoveItem(item.ID)}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove item"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ItemListComponent;
